import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Plus, Loader2, Search, Puzzle, Trash2, Filter, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "@/lib/toastBanner";
import usePersistentState from "@/hooks/usePersistentState";
import DeleteConfirmDialog from "@/components/DeleteConfirmDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
  mc: "Multiple Choice",
  sata: "Select All",
  task: "Task",
  likert: "Likert",
  external_score: "External Score",
  external_item: "External Item",
};
const TYPE_COLORS = {
  mc: "bg-blue-500/15 text-blue-400 border-blue-500/20",
  sata: "bg-purple-500/15 text-purple-400 border-purple-500/20",
  task: "bg-amber-500/15 text-amber-400 border-amber-500/20",
  likert: "bg-teal-500/15 text-teal-400 border-teal-500/20",
};

/** Styled toggle group for filter chips — mirrors Analytics filter pattern. */
function FilterToggle({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="flex gap-1 p-0.5 rounded-md" style={{ backgroundColor: "var(--admin-bg)" }}>
      {options.map(({ v, l }) => (
        <button
          key={v}
          onClick={() => onChange(v)}
          data-testid={testIdPrefix ? `${testIdPrefix}-${v}` : undefined}
          className="px-2.5 py-1 rounded text-[10px] font-body transition-colors"
          style={{
            backgroundColor: value === v ? "color-mix(in srgb, var(--primary-color, #4A90D9) 25%, transparent)" : "transparent",
            color: "var(--admin-container-text)",
            opacity: value === v ? 1 : 0.5,
            fontWeight: value === v ? 600 : 400,
          }}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function ItemBuilder() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [search, setSearch] = usePersistentState("items:search", "");
  const [showFilters, setShowFilters] = usePersistentState("items:showFilters", false);
  const [filterType, setFilterType] = usePersistentState("items:filterType", "all");
  const [filterCategory, setFilterCategory] = usePersistentState("items:filterCategory", "all");
  const [filterSource, setFilterSource] = usePersistentState("items:filterSource", "all");
  const [filterStatus, setFilterStatus] = usePersistentState("items:filterStatus", "all");

  const fetchItems = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/items`, { withCredentials: true });
      setItems(data);
    } catch {
      toast.error("Failed to load items");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleDelete = async () => {
    const item = deleteTarget;
    if (!item) return;
    try {
      await axios.delete(`${API}/items/${item.id}`, { withCredentials: true });
      toast.success("Item deleted");
      fetchItems();
    } catch {
      toast.error("Delete failed");
      throw new Error("delete failed");
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilterType("all");
    setFilterCategory("all");
    setFilterSource("all");
    setFilterStatus("all");
  };

  // Strip HTML tags to allow searching inside rich-text stem bodies too.
  const stripHtml = (html) => (html || "").replace(/<[^>]*>/g, " ").replace(/&nbsp;/g, " ");

  const filtered = items.filter((i) => {
    const q = search.trim().toLowerCase();
    const matchSearch =
      !q ||
      (i.title || "").toLowerCase().includes(q) ||
      stripHtml(i.stem).toLowerCase().includes(q);
    const matchType = filterType === "all" || i.item_type === filterType;
    const matchCategory = filterCategory === "all" || i.item_category === filterCategory;
    const matchSource = filterSource === "all" || i.scoring_source === filterSource;
    const matchStatus = filterStatus === "all" || i.status === filterStatus;
    return matchSearch && matchType && matchCategory && matchSource && matchStatus;
  });

  const activeFilterCount =
    (filterType !== "all" ? 1 : 0) +
    (filterCategory !== "all" ? 1 : 0) +
    (filterSource !== "all" ? 1 : 0) +
    (filterStatus !== "all" ? 1 : 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-[#6E7787]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="item-builder-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight" data-testid="item-builder-title">
            Question Bank
          </h1>
          <p className="text-sm text-[#6E7787] font-body mt-1">
            Create and manage reusable questions for learning experiences and assessments
          </p>
        </div>
        <Button
          onClick={() => navigate("/admin/items/new")}
          data-testid="create-item-button"
          className="font-body text-white h-10 px-4 rounded-xl"
          style={{ backgroundColor: "var(--primary-color, #4A90D9)", color: "var(--on-secondary, #FFFFFF)" }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Question
        </Button>
      </div>

      {/* Search + Filters action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#6E7787]" />
          <Input
            placeholder="Search title or question text…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="item-search-input"
            className="pl-10 bg-[#0A0C10] border-white/10 text-white placeholder:text-[#6E7787] h-10"
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            data-testid="toggle-filters-btn"
            className="text-xs h-9 px-3"
            style={{ borderColor: "rgba(255,255,255,0.1)", color: "#A1A9B8", backgroundColor: "transparent" }}
          >
            <Filter className="h-3.5 w-3.5 mr-1.5" />
            Filters
            {activeFilterCount > 0 && (
              <span
                className="ml-1.5 inline-flex items-center justify-center text-[10px] font-semibold rounded-full h-4 min-w-[16px] px-1 text-white"
                style={{ backgroundColor: "var(--primary-color, #4A90D9)" }}
                data-testid="active-filter-count"
              >
                {activeFilterCount}
              </span>
            )}
            {showFilters ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
          </Button>
          {(activeFilterCount > 0 || search) && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              data-testid="clear-filters-btn"
              className="text-xs h-9 px-3"
              style={{ borderColor: "rgba(255,255,255,0.1)", color: "#A1A9B8", backgroundColor: "transparent" }}
            >
              Clear
            </Button>
          )}
          <span className="text-xs font-body text-[#6E7787] ml-1" data-testid="item-count">
            {filtered.length} of {items.length} question{items.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Filters panel */}
      {showFilters && (
        <div className="bg-[#11141A] border border-white/10 rounded-xl p-4" data-testid="item-filters-panel">
          <div className="flex items-center gap-x-6 gap-y-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Label className="font-body text-xs text-[#A1A9B8]">Type</Label>
              <FilterToggle
                options={[
                  { v: "all", l: "All" },
                  { v: "mc", l: "MC" },
                  { v: "sata", l: "SATA" },
                  { v: "task", l: "Task" },
                  { v: "likert", l: "Likert" },
                  { v: "external_score", l: "Ext Score" },
                  { v: "external_item", l: "Ext Item" },
                ]}
                value={filterType}
                onChange={setFilterType}
                testIdPrefix="item-filter-type"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="font-body text-xs text-[#A1A9B8]">Category</Label>
              <FilterToggle
                options={[
                  { v: "all", l: "All" },
                  { v: "assessment", l: "Assessment" },
                  { v: "survey", l: "Survey" },
                ]}
                value={filterCategory}
                onChange={setFilterCategory}
                testIdPrefix="item-filter-category"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="font-body text-xs text-[#A1A9B8]">Source</Label>
              <FilterToggle
                options={[
                  { v: "all", l: "All" },
                  { v: "native", l: "Native" },
                  { v: "external", l: "External" },
                ]}
                value={filterSource}
                onChange={setFilterSource}
                testIdPrefix="item-filter-source"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="font-body text-xs text-[#A1A9B8]">Status</Label>
              <FilterToggle
                options={[
                  { v: "all", l: "All" },
                  { v: "published", l: "Published" },
                  { v: "draft", l: "Draft" },
                ]}
                value={filterStatus}
                onChange={setFilterStatus}
                testIdPrefix="item-filter-status"
              />
            </div>
          </div>
        </div>
      )}

      {/* Items List */}
      {filtered.length === 0 ? (
        <div className="text-center py-16 bg-[#11141A] border border-white/10 rounded-xl" data-testid="items-empty">
          <Puzzle className="h-10 w-10 text-[#6E7787] mx-auto mb-3" />
          <p className="text-[#6E7787] font-body">
            {items.length === 0
              ? "No questions yet. Create your first question to get started."
              : "No questions match the current filters."}
          </p>
          {items.length === 0 ? (
            <Button onClick={() => navigate("/admin/items/new")} variant="ghost" className="mt-3 text-sm font-body" style={{ color: "var(--primary-color, #4A90D9)" }}>
              Create your first question
            </Button>
          ) : (
            <Button onClick={clearFilters} variant="ghost" className="mt-3 text-sm font-body" style={{ color: "var(--primary-color, #4A90D9)" }} data-testid="empty-clear-btn">
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-[#11141A] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full" data-testid="items-table">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Title</th>
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden sm:table-cell">Type</th>
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden sm:table-cell">Category</th>
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden md:table-cell">Topic</th>
                <th className="text-left px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden md:table-cell">Source</th>
                <th className="text-center px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider hidden sm:table-cell">Pts</th>
                <th className="text-center px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-3 text-xs font-body font-medium text-[#6E7787] uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((item) => (
                <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02] transition-colors cursor-pointer" onClick={() => navigate(`/admin/items/${item.id}`)} data-testid={`item-row-${item.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-white font-body font-medium truncate max-w-xs">{item.title}</p>
                      </div>
                    <p className="text-xs text-[#6E7787] font-body truncate max-w-xs mt-0.5">{stripHtml(item.stem).substring(0, 60)}</p>
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <Badge className={`text-xs font-body ${TYPE_COLORS[item.item_type] || "bg-white/5 text-[#A1A9B8]"}`}>
                      {TYPE_LABELS[item.item_type] || item.item_type}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 hidden sm:table-cell">
                    <Badge className={item.item_category === "survey" ? "bg-cyan-500/15 text-cyan-400 border-cyan-500/20 text-xs font-body" : "bg-white/5 text-[#A1A9B8] border-white/10 text-xs font-body"}>
                      {item.item_category === "survey" ? "Survey" : "Assessment"}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-sm text-[#A1A9B8] font-body">{(item.meta_tag_names || []).join(", ") || "—"}</span>
                  </td>
                  <td className="px-5 py-3.5 hidden md:table-cell">
                    <span className="text-sm text-[#A1A9B8] font-body capitalize">{item.scoring_source}</span>
                  </td>
                  <td className="px-5 py-3.5 text-center hidden sm:table-cell">
                    <span className="text-sm text-white font-body font-medium">{item.point_value}</span>
                  </td>
                  <td className="px-5 py-3.5 text-center">
                    <Badge className={item.status === "published" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/20" : "bg-amber-500/15 text-amber-400 border-amber-500/20"}>
                      {item.status}
                    </Badge>
                  </td>
                  <td className="px-5 py-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => setDeleteTarget(item)} className="p-2 text-[#6E7787] hover:text-red-400 transition-colors rounded-lg hover:bg-red-500/10" data-testid={`item-delete-${item.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                      </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DeleteConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        tier={1}
        entityName={deleteTarget?.title}
        onConfirm={handleDelete}
      />
    </div>
  );
}
